import { Trophy, MapPin, Clock, ArrowRight, Heart, Facebook, Instagram, Twitter, Star, Medal, Users, HandHeart } from "lucide-react";

// Bespoke pitch demo for Memphis Soldiers (non-profit junior baseball & softball org).
// Front page + menu only — ALL links/buttons decorative. Modeled on the Sox baseball club theme.
// No TraceElements/Emergent branding.

const PHOTOS = {
  heroBoys: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/a5bd62432533b85b_70292607581438272.webp",
  boysTeam: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/e9e087a0e5cacbfc_Screenshot_20261024_111901_Gallery.jpg.jpeg",
  girlsFence: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/61b86df6395041d4_Screenshot_20261024_112410_Messages.jpg.jpeg",
  girlsStadium: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/b048139ef2ce1181_Screenshot_20260913_195408_GroupMe.jpg.jpeg",
  playerSpotlight: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/adc4907e0ae6d2ef_6885406389645031807.webp",
};

const PURPLE = "#3B2273";
const GOLD = "#F2B80C";

const MENU = ["Home", "About", "Teams", "Schedule", "News", "Sponsors", "Contact"];

const GAMES = [
  { date: "Sat · Jun 14", time: "10:00 AM", league: "Boys 12U Baseball", match: "Memphis Soldiers vs River City Wildcats", location: "Gaisman Park" },
  { date: "Sun · Jun 15", time: "1:00 PM", league: "Lady Soldiers 14U Softball", match: "Lady Soldiers vs Bluff City Blaze", location: "Willow Park" },
  { date: "Sat · Jun 21", time: "9:00 AM", league: "Boys 10U Baseball", match: "Memphis Soldiers vs Germantown Giants", location: "Tobey Field" },
];

const NEWS = [
  { title: "Soldiers sweep the weekend doubleheader at Gaisman Park", date: "Jun 8, 2026", tag: "Boys Baseball" },
  { title: "Lady Soldiers punch their ticket to the regional tournament", date: "Jun 2, 2026", tag: "Girls Softball" },
  { title: "Fall ball registration opens soon — every kid plays, regardless of income", date: "May 27, 2026", tag: "Community" },
];

const noop = (e) => e.preventDefault();

export default function MemphisSoldiersDemo() {
  return (
    <div className="min-h-screen bg-[#120B1E] text-[#F5F2FA] font-jakarta" data-testid="soldiers-demo">
      {/* ===== TOP BAR ===== */}
      <div className="text-[#120B1E] text-center py-2 px-4 text-[11px] font-extrabold uppercase tracking-[0.2em]" style={{ backgroundColor: GOLD }} data-testid="soldiers-top-bar">
        Non-profit youth baseball &amp; softball · Memphis, TN · Every kid plays
      </div>

      {/* ===== HEADER + MENU (decorative) ===== */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-[#120B1E]/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-[76px] flex items-center justify-between gap-6">
          <a href="#" onClick={noop} className="flex items-center gap-3" data-testid="soldiers-logo">
            <span className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: GOLD }}>
              <Trophy size={20} className="text-[#120B1E]" />
            </span>
            <span className="font-display font-bold text-xl tracking-tight leading-none">
              Memphis <span style={{ color: GOLD }}>Soldiers</span>
              <span className="block text-[10px] font-semibold uppercase tracking-[0.28em] text-white/50 mt-1">Junior Baseball · Softball</span>
            </span>
          </a>
          <nav className="hidden lg:flex items-center gap-7 text-[13px] font-bold uppercase tracking-wider" data-testid="soldiers-menu">
            {MENU.map((item) => (
              <a key={item} href="#" onClick={noop} className="text-white/75 hover:text-[#F2B80C] transition-colors" data-testid={`soldiers-menu-${item.toLowerCase()}`}>
                {item}
              </a>
            ))}
          </nav>
          <a href="#" onClick={noop} className="hidden sm:inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#120B1E] transition-transform hover:-translate-y-0.5" style={{ backgroundColor: GOLD }} data-testid="soldiers-donate-header">
            <Heart size={13} fill="currentColor" /> Donate
          </a>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" data-testid="soldiers-hero">
        <div className="absolute inset-0">
          <img src={PHOTOS.heroBoys} alt="Memphis Soldiers boys baseball team" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#120B1E] via-[#120B1E]/80 to-[#120B1E]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#120B1E] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-40">
          <div className="inline-flex items-center gap-2 border px-4 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-[0.24em]" style={{ borderColor: GOLD, color: GOLD }} data-testid="soldiers-hero-badge">
            <Medal size={13} /> Home of the Soldiers &amp; Lady Soldiers
          </div>
          <h1 className="mt-7 font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] max-w-3xl" data-testid="soldiers-hero-headline">
            Building Soldiers.<br /><span style={{ color: GOLD }}>On the Field.</span><br />Off the Field.
          </h1>
          <p className="mt-6 text-base lg:text-lg leading-relaxed text-white/70 max-w-xl" data-testid="soldiers-hero-subheadline">
            A non-profit junior baseball and softball organization developing Memphis youth —
            boys and girls — through discipline, teamwork, and the love of the game.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#" onClick={noop} className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] text-[#120B1E] transition-transform hover:-translate-y-0.5" style={{ backgroundColor: GOLD, boxShadow: "0 12px 32px rgba(242,184,12,0.35)" }} data-testid="soldiers-hero-cta-join">
              Join the Team <ArrowRight size={15} />
            </a>
            <a href="#" onClick={noop} className="inline-flex items-center gap-2 border border-white/30 px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] text-white hover:border-[#F2B80C] hover:text-[#F2B80C] transition-colors" data-testid="soldiers-hero-cta-support">
              Support Our Players
            </a>
          </div>
        </div>
      </section>

      {/* ===== STATS RIBBON ===== */}
      <section className="border-y border-white/10" style={{ backgroundColor: PURPLE }} data-testid="soldiers-stats">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { stat: "2", label: "Teams — Boys & Girls" },
            { stat: "30+", label: "Young Athletes" },
            { stat: "100%", label: "Volunteer-Run" },
            { stat: "0", label: "Kids Turned Away" },
          ].map((m) => (
            <div key={m.label} className="text-center" data-testid={`soldiers-stat-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="font-display text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: GOLD }}>{m.stat}</div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-white/60 font-semibold">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== UPCOMING GAMES ===== */}
      <section className="py-16 lg:py-24" data-testid="soldiers-schedule">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-[0.28em]" style={{ color: GOLD }}>Upcoming Games</div>
              <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight">This Week at the Park</h2>
            </div>
            <a href="#" onClick={noop} className="text-[11px] font-extrabold uppercase tracking-[0.2em] inline-flex items-center gap-2 text-white/70 hover:text-[#F2B80C] transition-colors" data-testid="soldiers-schedule-all">
              Full Schedule <ArrowRight size={14} />
            </a>
          </div>
          <div className="space-y-4">
            {GAMES.map((g, i) => (
              <a key={g.match} href="#" onClick={noop} className="group grid grid-cols-1 lg:grid-cols-12 gap-3 items-center bg-white/5 border border-white/10 hover:border-[#F2B80C]/60 rounded-2xl px-6 py-5 transition-colors" data-testid={`soldiers-game-${i}`}>
                <div className="lg:col-span-2">
                  <div className="font-display font-bold text-lg" style={{ color: GOLD }}>{g.date}</div>
                  <div className="flex items-center gap-1.5 text-xs text-white/50 mt-1"><Clock size={11} /> {g.time}</div>
                </div>
                <div className="lg:col-span-2 text-[10px] font-extrabold uppercase tracking-[0.2em] text-white/45">{g.league}</div>
                <div className="lg:col-span-6 font-display text-lg lg:text-xl font-bold tracking-tight group-hover:text-[#F2B80C] transition-colors">{g.match}</div>
                <div className="lg:col-span-2 flex items-center gap-1.5 text-xs text-white/55"><MapPin size={12} style={{ color: GOLD }} /> {g.location}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== OUR TEAMS ===== */}
      <section className="py-16 lg:py-24 bg-[#0D0817] border-y border-white/10" data-testid="soldiers-teams">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.28em]" style={{ color: GOLD }}>Our Teams</div>
            <h2 className="mt-3 font-display text-3xl lg:text-5xl font-bold tracking-tight">One Organization. Two Squads.</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[
              { name: "Soldiers Baseball", sub: "Boys · 10U & 12U", img: PHOTOS.boysTeam, testid: "soldiers-team-boys" },
              { name: "Lady Soldiers Softball", sub: "Girls · 12U & 14U", img: PHOTOS.girlsFence, testid: "soldiers-team-girls" },
            ].map((t) => (
              <a key={t.name} href="#" onClick={noop} className="group relative overflow-hidden rounded-3xl block" data-testid={t.testid}>
                <img src={t.img} alt={t.name} loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#120B1E] via-[#120B1E]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7 flex items-end justify-between">
                  <div>
                    <div className="font-display text-2xl font-bold tracking-tight">{t.name}</div>
                    <div className="text-xs uppercase tracking-[0.22em] font-bold mt-1.5" style={{ color: GOLD }}>{t.sub}</div>
                  </div>
                  <span className="h-11 w-11 rounded-full flex items-center justify-center text-[#120B1E] transition-transform group-hover:-translate-y-1" style={{ backgroundColor: GOLD }}>
                    <ArrowRight size={17} />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MISSION ===== */}
      <section className="py-16 lg:py-24" data-testid="soldiers-mission">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative max-w-[420px]">
              <div className="absolute -inset-3 rounded-[36px] opacity-25" style={{ backgroundColor: PURPLE }} />
              <img src={PHOTOS.playerSpotlight} alt="Young Memphis Soldiers player" loading="lazy" className="relative w-full rounded-[28px] object-cover aspect-[4/5] border border-white/10" data-testid="soldiers-mission-image" />
              <div className="absolute -right-4 bottom-8 rounded-2xl px-5 py-4 shadow-2xl" style={{ backgroundColor: GOLD }}>
                <div className="font-display text-xl font-bold text-[#120B1E] leading-none">Every Kid</div>
                <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#120B1E]/70 mt-1">Plays. Period.</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.28em]" style={{ color: GOLD }}>Our Mission</div>
            <h2 className="mt-4 font-display text-3xl lg:text-5xl font-bold tracking-tight leading-tight" data-testid="soldiers-mission-heading">
              More Than Baseball.
            </h2>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-white/65 max-w-2xl">
              Memphis Soldiers is a 100% volunteer-run non-profit. We put gloves in hands, not price tags on dreams —
              developing young athletes into students, teammates, and leaders across Memphis.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                { icon: HandHeart, text: "Free equipment and uniforms for every registered player" },
                { icon: Users, text: "Mentorship and academic accountability built into every season" },
                { icon: Star, text: "Boys' baseball and girls' softball — one family, one standard" },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <li key={i} className="flex items-start gap-4" data-testid={`soldiers-mission-point-${i}`}>
                    <span className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${GOLD}1f`, border: `1px solid ${GOLD}55` }}>
                      <Icon size={17} style={{ color: GOLD }} />
                    </span>
                    <span className="text-sm lg:text-base text-white/75 pt-2">{item.text}</span>
                  </li>
                );
              })}
            </ul>
            <a href="#" onClick={noop} className="mt-9 inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.2em] hover:text-[#F2B80C] transition-colors" data-testid="soldiers-mission-cta">
              Read Our Story <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ===== NEWS ===== */}
      <section className="py-16 lg:py-24 bg-[#0D0817] border-y border-white/10" data-testid="soldiers-news">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-[0.28em]" style={{ color: GOLD }}>Latest News</div>
              <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight">From the Dugout</h2>
            </div>
            <a href="#" onClick={noop} className="text-[11px] font-extrabold uppercase tracking-[0.2em] inline-flex items-center gap-2 text-white/70 hover:text-[#F2B80C] transition-colors" data-testid="soldiers-news-all">
              All News <ArrowRight size={14} />
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {NEWS.map((n, i) => (
              <a key={n.title} href="#" onClick={noop} className="group bg-white/5 border border-white/10 hover:border-[#F2B80C]/60 rounded-2xl p-7 transition-colors" data-testid={`soldiers-news-${i}`}>
                <span className="inline-block text-[10px] font-extrabold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full" style={{ backgroundColor: `${GOLD}1f`, color: GOLD }}>{n.tag}</span>
                <h3 className="mt-5 font-display text-lg font-bold tracking-tight leading-snug group-hover:text-[#F2B80C] transition-colors">{n.title}</h3>
                <div className="mt-4 text-xs text-white/45 font-semibold">{n.date}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SPONSOR / DONATE BAND ===== */}
      <section className="py-16 lg:py-20" data-testid="soldiers-sponsor">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-[32px] px-8 lg:px-16 py-14 lg:py-16 text-[#120B1E]" style={{ backgroundColor: GOLD }}>
            <div className="absolute inset-0 opacity-15">
              <img src={PHOTOS.girlsStadium} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative max-w-2xl">
              <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-[#120B1E]/60">Sponsors &amp; Donors</div>
              <h2 className="mt-4 font-display text-3xl lg:text-5xl font-bold tracking-tight leading-tight" data-testid="soldiers-sponsor-heading">
                Fuel the Next Inning.
              </h2>
              <p className="mt-4 text-sm lg:text-base leading-relaxed text-[#120B1E]/75 font-medium">
                Every dollar goes straight to gloves, jerseys, field time and tournament fees.
                Sponsor a Soldier — and put your business on the fence where Memphis families gather.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href="#" onClick={noop} className="inline-flex items-center gap-2 bg-[#120B1E] text-white px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5" data-testid="soldiers-sponsor-cta">
                  Become a Sponsor <ArrowRight size={15} />
                </a>
                <a href="#" onClick={noop} className="inline-flex items-center gap-2 border-2 border-[#120B1E]/30 px-8 py-4 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] hover:bg-[#120B1E]/10 transition-colors" data-testid="soldiers-donate-cta">
                  Donate Any Amount
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER (decorative) ===== */}
      <footer className="bg-[#0A0613] border-t border-white/10" data-testid="soldiers-footer">
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: GOLD }}>
                <Trophy size={18} className="text-[#120B1E]" />
              </span>
              <span className="font-display font-bold text-lg tracking-tight">Memphis <span style={{ color: GOLD }}>Soldiers</span></span>
            </div>
            <p className="mt-4 text-sm text-white/55 leading-relaxed">
              Non-profit junior baseball and softball. Developing Memphis youth on the field and off it.
            </p>
            <div className="mt-5 flex items-center gap-3">
              {[Facebook, Instagram, Twitter].map((Icon, i) => (
                <a key={i} href="#" onClick={noop} aria-label="Social link" className="h-9 w-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#F2B80C] hover:text-[#F2B80C] transition-colors" data-testid={`soldiers-social-${i}`}>
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-white/40 mb-4">Teams</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {["Soldiers 12U Baseball", "Soldiers 10U Baseball", "Lady Soldiers 14U", "Lady Soldiers 12U", "Fall Ball"].map((l) => (
                <li key={l}><a href="#" onClick={noop} className="hover:text-[#F2B80C] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-white/40 mb-4">Organization</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {["Our Mission", "Coaches & Volunteers", "Sponsorships", "Registration", "Code of Conduct"].map((l) => (
                <li key={l}><a href="#" onClick={noop} className="hover:text-[#F2B80C] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-white/40 mb-4">Contact</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li className="flex items-center gap-2"><MapPin size={13} style={{ color: GOLD }} /> Memphis, Tennessee</li>
              {["General Inquiries", "Coach a Team", "Field Locations"].map((l) => (
                <li key={l}><a href="#" onClick={noop} className="hover:text-[#F2B80C] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-white/40">© 2026 Memphis Soldiers. All rights reserved.</span>
            <span className="text-xs text-white/40">A 501(c)(3) non-profit youth sports organization.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
